import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import {
  NotificationScope,
  NotificationSource,
  NotificationTarget,
} from '../../../../../../core/enums/enums';
import { CompaniesService } from '../../../../../../core/management';
import { Observable, of, Subscription } from 'rxjs';
import { map, startWith, tap } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { AppState } from '../../../../../../core/reducers';
import {
  CreateNotificationOnServer,
  Notification,
  NotificationCategory,
  NotificationChannel,
  NotificationService,
  UpdateNotificationOnServer,
} from '../../../../../../core/lists';

@Component({
  selector: 'kt-create-notification',
  templateUrl: './create-notification.component.html',
})
export class CreateNotificationComponent implements OnInit, OnDestroy {
  private subscriptions = new Subscription();
  form = new FormGroup({
    description: new FormControl('', Validators.required),
    name: new FormControl('', Validators.required),
    template: new FormControl('', Validators.required),
    trigger: new FormControl('', Validators.required),
    companyId: new FormControl(''),
    source: new FormControl('', Validators.required),
    scope: new FormControl('', Validators.required),
    target: new FormControl('', Validators.required),
    category: new FormControl('', Validators.required),
    channels: new FormControl([]),
    channelSearch: new FormControl(''),
  });
  sources = Object.values(NotificationSource);
  scopes = Object.values(NotificationScope);
  targets = Object.values(NotificationTarget);
  triggers = [];
  companies = [];
  isLoadingCategories = false;
  categories: NotificationCategory[] = [];
  filteredCategories$: Observable<NotificationCategory[]> = of([]);
  channels: NotificationChannel[] = [];
  filteredChannels$: Observable<NotificationChannel[]> = of([]);
  @ViewChild('search', { static: true }) search: ElementRef;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: Notification,
    public dialogRef: MatDialogRef<CreateNotificationComponent>,
    private companiesService: CompaniesService,
    private notificationService: NotificationService,
    private store: Store<AppState>,
    private cdr: ChangeDetectorRef,
  ) {
    this.companiesService.getTriggers().subscribe((res) => {
      this.triggers = res;
      this.cdr.detectChanges();
    });
  }

  ngOnInit(): void {
    this.form
      .get('companyId')
      .valueChanges.pipe(tap((id) => this.getCategories(id)))
      .subscribe();

    this.filteredCategories$ = this.form.get('category').valueChanges.pipe(
      startWith(''),
      map((value) => (typeof value === 'string' ? value : (value as NotificationCategory).name)),
      map((name) =>
        name
          ? (this.filter(this.categories, name) as unknown as NotificationCategory[])
          : this.categories.slice(),
      ),
    );

    this.filteredChannels$ = this.form.get('channelSearch').valueChanges.pipe(
      startWith(''),
      map((value) => (typeof value === 'string' ? value : (value as NotificationChannel).name)),
      map((name) => (name ? this.filter(this.channels, name) : this.channels.slice())),
    );

    this.getCategories();

    if (this.data.id) {
      this.getCompanies();

      this.form.get('description').patchValue(this.data.description);
      this.form.get('name').patchValue(this.data.name);
      this.form.get('template').patchValue(this.data.template);
      this.form.get('trigger').patchValue(this.data.trigger);
      this.form.get('companyId').patchValue(this.data.category.company_id);
      this.form.get('source').patchValue(this.data.source);
      this.form.get('scope').patchValue(this.data.scope);
      this.form.get('target').patchValue(this.data.target);
      this.form.get('category').patchValue(this.data.category);
      this.form.get('channels').patchValue(this.data.channels);
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  getTitle(): string {
    return `NOTIFICATION.${this.data.id ? 'UPDATE_TITLE' : 'ADD_TITLE'}`;
  }

  getCompanies(): void {
    if (this.companies.length) return;

    this.subscriptions.add(
      this.companiesService.getAllCompanies().subscribe((res) => (this.companies = res)),
    );
  }

  getCategories(id = ''): void {
    this.isLoadingCategories = true;
    this.subscriptions.add(
      this.notificationService.getCategories(id || '').subscribe((res) => {
        this.categories = res;
        this.isLoadingCategories = false;
      }),
    );
  }

  getChannels(event): void {
    event.stopPropagation();

    if (this.channels.length) return;

    this.subscriptions.add(
      this.notificationService.getChannels().subscribe((res) => (this.channels = res)),
    );
  }

  removeChannel(channel: NotificationChannel): void {
    const channels = this.form.get('channels');
    const newChannels = channels.value.filter((ch) => ch.id !== channel.id);

    channels.patchValue(newChannels);
  }

  selectChannel(event): void {
    const selected = event.option.value;
    const channels = this.form.get('channels');
    const isExist = channels.value.some((ch) => ch.id === selected.id);

    if (isExist) {
      this.search.nativeElement.value = '';

      return;
    }

    const newChannels = [...channels.value];

    newChannels.push(selected);
    channels.patchValue(newChannels);
    this.search.nativeElement.value = '';
  }

  clearSearch(event) {
    event.stopPropagation();

    this.search.nativeElement.value = '';
  }

  filter(
    items: NotificationCategory[] | NotificationChannel[],
    name: string,
  ): NotificationCategory[] | NotificationChannel[] {
    const value = name.toLowerCase();

    return items.filter((item) => item.name.toLowerCase().includes(value));
  }

  display(item: NotificationCategory | NotificationChannel): string {
    return item && item.name ? item.name : '';
  }

  isInvalid(name: string): boolean {
    const field = this.form.get(name);

    if (Array.isArray(field.value)) {
      field.value.length ? field.setErrors(null) : field.setErrors({ required: true });
    }

    return field.touched && field.invalid;
  }

  getErrorMessage(name: string): string {
    return `${name} field is required`;
  }

  close(event) {
    event.stopPropagation();

    this.dialogRef.close();
  }

  submit(event) {
    event.stopPropagation();

    this.createUpdateNotification();
  }

  createUpdateNotification() {
    const notification = new Notification(
      this.data.id,
      this.form.get('description').value,
      this.form.get('source').value,
      this.form.get('scope').value,
      this.form.get('target').value,
      this.form.get('category').value,
      this.form.get('channels').value,
      this.form.get('name').value,
      this.form.get('template').value,
      this.form.get('trigger').value,
    );

    for (const [key, value] of Object.entries(notification)) {
      if (typeof value === 'string') {
        notification[key] = value.trim();
      }
    }

    this.data.id
      ? this.store.dispatch(new UpdateNotificationOnServer({ notification }))
      : this.store.dispatch(new CreateNotificationOnServer({ notification }));

    this.dialogRef.close({ notification });
  }
}
